"use client";

import { FiFilter } from "react-icons/fi";
import { useT } from "@/hooks/useT";

// Mirrors the parent's filter type; "all" is silently ignored by the
// two visible buttons but kept so the component can accept the parent's
// state without a cast.
export type StatusFilter = "all" | "active" | "closed";

interface StatusFilterButtonsProps {
  filter: StatusFilter;
  setFilter: (next: StatusFilter) => void;
  /** "mobile" → compact pill buttons, no label. "desktop" → FiFilter icon + "Status:" label + larger buttons. */
  variant?: "mobile" | "desktop";
}

/**
 * Active / Closed status filter pills used in the markets toolbar.
 *
 * Mobile and desktop variants share the same toggle behaviour, only
 * the chrome differs: mobile is compact (no label, smaller text +
 * padding, hardcoded "Active"/"Closed"); desktop adds an FiFilter
 * icon, the i18n label "Status:" and uses i18n for the button copy.
 */
export function StatusFilterButtons({ filter, setFilter, variant = "desktop" }: StatusFilterButtonsProps) {
  const t = useT();

  if (variant === "mobile") {
    return (
      <div className="flex gap-1 bg-black/[0.03] dark:bg-white/[0.04] rounded-[5px] p-0.5 flex-shrink-0">
        <button
          onClick={() => setFilter("active")}
          className={`px-2.5 py-1 rounded-[5px] text-[11px] font-semibold transition-colors ${
            filter === "active" ? "bg-emerald-500 text-white" : "text-gray-500"
          }`}
        >
          Active
        </button>
        <button
          onClick={() => setFilter("closed")}
          className={`px-2.5 py-1 rounded-[5px] text-[11px] font-semibold transition-colors ${
            filter === "closed" ? "bg-[#FF1744] text-white" : "text-gray-500"
          }`}
        >
          Closed
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <FiFilter className="text-gray-500 dark:text-gray-400 w-4 h-4" />
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t("filterBar.status")}:</span>
      <div className="flex gap-1 bg-black/[0.03] dark:bg-white/[0.04] rounded-[5px] p-0.5">
        <button
          onClick={() => setFilter("active")}
          className={`relative px-4 py-1.5 rounded-[5px] text-xs font-semibold transition-colors ${
            filter === "active"
              ? "bg-emerald-500 text-white"
              : "text-gray-600 dark:text-gray-400 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"
          }`}
        >
          {t("filterBar.statusActive")}
        </button>
        <button
          onClick={() => setFilter("closed")}
          className={`relative px-4 py-1.5 rounded-[5px] text-xs font-semibold transition-colors ${
            filter === "closed"
              ? "bg-[#FF1744] text-white"
              : "text-gray-600 dark:text-gray-400 hover:text-[#FF1744] hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"
          }`}
        >
          {t("filterBar.statusClosed")}
        </button>
      </div>
    </div>
  );
}
